import { z } from 'zod';
import { CAL_EVENT_MAX_CHECKLIST_ITEMS } from '../../events/cal-event-max-checklist-items.js';
import { CAL_EVENT_MAX_PRODUCTS } from '../../events/cal-event-max-products.js';
import { CAL_EVENT_TEMPLATE_MAX_CATEGORIES } from '../../events/cal-event-template-max-categories.js';
import { CAL_EVENT_TEMPLATE_MAX_DURATION_MINUTES } from '../../events/cal-event-template-max-duration-minutes.js';
import { CAL_EVENT_TEMPLATE_MAX_FIELDS } from '../../events/cal-event-template-max-fields.js';
import { CAL_EVENT_TEMPLATE_MAX_RULES } from '../../events/cal-event-template-max-rules.js';
import { isAuthorableFieldType } from '../../events/is-authorable-field-type.js';
import { CORE_FIELD_ID_SET } from '../../events/rule-types.js';
import { validateRules } from '../../events/rule-validator.js';
import { STORE_REQUIREMENT_NAMES } from '../../events/store-requirement-names.js';
import { TEMPLATE_FIELD_CONFIG_SCHEMAS } from '../../events/template-field-config.js';
import { optionalTrimmedNullableString, optionalUuidString, requiredUuidString, } from '../zod-helpers/index.js';
import { calEventChecklistItemSchema } from './cal-event-checklist-item-schema.js';
import { calEventTemplateSettingsShape } from './cal-event-template-settings-shape.js';
import { CAL_EVENT_VENUE_LIMITS } from './cal-event-venue-limits.js';
import { refineFieldDeadline } from './refine-field-deadline.js';
import { refinePerProductPhase } from './refine-per-product-phase.js';
import { refineProductSummaryColumns } from './refine-product-summary-columns.js';
import { refineTemplateSettings } from './refine-template-settings.js';
import { templateRuleInputSchema } from './template-rule-input-schema.js';
const templateFieldInputSchema = z
    .object({
    key: z
        .string()
        .regex(/^[a-z][a-zA-Z0-9_]{0,63}$/, 'Field key must start with a letter and use only letters, digits, or _.')
        .refine((key) => !CORE_FIELD_ID_SET.has(key), 'This field key is reserved.'),
    label: z
        .string()
        .trim()
        .min(1, 'Field label is required.')
        .max(128, 'Field label must be 128 characters or fewer.'),
    typeId: z.number().int().refine(isAuthorableFieldType, 'Unsupported field type.'),
    config: z.unknown(),
})
    .transform((field, context) => {
    if (!isAuthorableFieldType(field.typeId))
        return z.NEVER;
    const parsed = TEMPLATE_FIELD_CONFIG_SCHEMAS[field.typeId].safeParse(field.config ?? {});
    if (!parsed.success) {
        for (const issue of parsed.error.issues) {
            context.addIssue({
                code: 'custom',
                message: issue.message,
                path: ['config', ...issue.path],
            });
        }
        return z.NEVER;
    }
    refineFieldDeadline(parsed.data, context, ['config']);
    refinePerProductPhase({ typeId: field.typeId, config: parsed.data }, context, ['config']);
    return { ...field, typeId: field.typeId, config: parsed.data };
});
const templateVenueSchema = z.object({
    label: optionalTrimmedNullableString(CAL_EVENT_VENUE_LIMITS.name, `Venue name must be ${CAL_EVENT_VENUE_LIMITS.name} characters or fewer.`),
    address: optionalTrimmedNullableString(CAL_EVENT_VENUE_LIMITS.address, `Address must be ${CAL_EVENT_VENUE_LIMITS.address} characters or fewer.`),
    city: optionalTrimmedNullableString(CAL_EVENT_VENUE_LIMITS.city, `City must be ${CAL_EVENT_VENUE_LIMITS.city} characters or fewer.`),
    zip: optionalTrimmedNullableString(CAL_EVENT_VENUE_LIMITS.zip, `Zip must be ${CAL_EVENT_VENUE_LIMITS.zip} characters or fewer.`),
    stateId: optionalUuidString('Invalid state.').optional().nullable(),
    countryId: optionalUuidString('Invalid country.').optional().nullable(),
});
const templateBodyShape = {
    name: z
        .string()
        .trim()
        .min(1, 'Template name is required.')
        .max(128, 'Template name must be 128 characters or fewer.'),
    defaultEventName: optionalTrimmedNullableString(128),
    prefixStoreName: z.boolean().optional(),
    defaultDurationMinutes: z
        .number()
        .int('Duration must be a whole number of minutes.')
        .min(1, 'Duration must be at least 1 minute.')
        .max(CAL_EVENT_TEMPLATE_MAX_DURATION_MINUTES, 'Duration must be 7 days or less.')
        .optional()
        .nullable(),
    checkInRequired: z.boolean().optional().default(false),
    needsApproval: z.boolean().optional().default(false),
    underReviewRequired: z.boolean().optional().default(false),
    photoRequired: z.boolean().optional().default(false),
    storeRequirement: z.enum(STORE_REQUIREMENT_NAMES).optional().default('optional'),
    requiresProductSummary: z.boolean().optional().default(false),
    ...calEventTemplateSettingsShape,
    venue: templateVenueSchema.optional().nullable(),
    checklistItems: z
        .array(calEventChecklistItemSchema)
        .max(CAL_EVENT_MAX_CHECKLIST_ITEMS, `A template can have at most ${CAL_EVENT_MAX_CHECKLIST_ITEMS} checklist items.`)
        .optional()
        .default([]),
    productIds: z
        .array(requiredUuidString('Product is required.', 'Invalid product ID.'))
        .max(CAL_EVENT_MAX_PRODUCTS, `A template can have at most ${CAL_EVENT_MAX_PRODUCTS} products.`)
        .refine((ids) => new Set(ids).size === ids.length, 'Each product can only be added once.')
        .optional()
        .default([]),
    categoryIds: z
        .array(requiredUuidString('Category is required.', 'Invalid category ID.'))
        .max(CAL_EVENT_TEMPLATE_MAX_CATEGORIES, `A template can have at most ${CAL_EVENT_TEMPLATE_MAX_CATEGORIES} categories.`)
        .refine((ids) => new Set(ids).size === ids.length, 'Each category can only be added once.')
        .optional()
        .default([]),
    includeUncategorizedProducts: z.boolean().optional().default(false),
    excludedProductIds: z
        .array(requiredUuidString('Product is required.', 'Invalid product ID.'))
        .max(CAL_EVENT_MAX_PRODUCTS, `A template can exclude at most ${CAL_EVENT_MAX_PRODUCTS} products.`)
        .refine((ids) => new Set(ids).size === ids.length, 'Each product can only be excluded once.')
        .optional()
        .default([]),
    fields: z
        .array(templateFieldInputSchema)
        .max(CAL_EVENT_TEMPLATE_MAX_FIELDS, `A template can have at most ${CAL_EVENT_TEMPLATE_MAX_FIELDS} fields.`)
        .optional()
        .default([]),
    rules: z
        .array(templateRuleInputSchema)
        .max(CAL_EVENT_TEMPLATE_MAX_RULES, `A template can have at most ${CAL_EVENT_TEMPLATE_MAX_RULES} rules.`)
        .optional()
        .default([]),
};
function refineTemplateBody(data, context) {
    const picked = new Set(data.productIds);
    data.excludedProductIds.forEach((productId, index) => {
        if (picked.has(productId)) {
            context.addIssue({
                code: 'custom',
                message: 'A product cannot be both picked and excluded.',
                path: ['excludedProductIds', index],
            });
        }
    });
    refineTemplateSettings(data, context);
    refineProductSummaryColumns(data, context);
    const seenKeys = new Set();
    data.fields.forEach((field, index) => {
        if (seenKeys.has(field.key)) {
            context.addIssue({
                code: 'custom',
                message: 'Field keys must be unique.',
                path: ['fields', index, 'key'],
            });
        }
        seenKeys.add(field.key);
    });
    const { errors } = validateRules(data.fields.map((field) => ({ id: field.key, type: field.typeId, config: field.config })), data.rules);
    for (const error of errors) {
        context.addIssue({ code: 'custom', message: error.message, path: ['rules', error.ruleIndex] });
    }
}
/** Template body plus the caller's identifying fields (brandId on create, templateVersionId on update). */
export function withCalEventTemplateBody(extraShape) {
    return z
        .object({ ...templateBodyShape, ...extraShape })
        .superRefine((data, context) => refineTemplateBody(data, context));
}
//# sourceMappingURL=with-cal-event-template-body.js.map